import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { unApi } from '../../shared/common/api/endpoints/reports/unApi';
import { showSuccessMessage, showErrorMessage } from './ui';

const CUSTOM_TOKEN_RE = /^_\d+_$/;

interface UsedInMetric {
    metric: string;
    id: number;
    name: string | null;
}

interface MissingMetric {
    metric: string;
    id: number;
    used_in: UsedInMetric[];
}

const DELETED_METRICS_CODE = 254;

/**
 * Признак ответа «отчёт ссылается на удалённые метрики».
 * Внимание: ответ приходит с HTTP-статусом 200, признак — поле code в теле.
 */
export const isDeletedMetricsError = (data: any): boolean =>
    !!data && (data.code === DELETED_METRICS_CODE || data.error === 'report_has_deleted_metrics');

/**
 * Имя метрики по токену dataKey: сначала ищем в локальном пуле метрик,
 * иначе показываем номер (например для чужого отчёта, где метрики нет локально).
 */
const resolveMetricName = (dataKey: string, id: number, pool?: any[]): string => {
    const found = Array.isArray(pool)
        ? pool.find((item) => item && item.dataKey === dataKey)
        : undefined;
    return found?.name || found?.custom_metric_name || `#${id}`;
};

/**
 * Убирает токены удалённых метрик (_ID_) из выбранных метрик в localStorage
 * (ключи tableMetricsItemSelected_*), чтобы мёртвые колонки не оставались в UI.
 */
/**
 * Существующие токены кастомных метрик (_ID_) из справочника createdMetrics в Redux.
 */
const getExistingCustomTokens = (state: any): Set<string> => {
    const children = state?.table?.createdMetrics?.[0]?.children ?? [];
    const set = new Set<string>();
    children.forEach((item: any) => {
        if (item?.dataKey) set.add(item.dataKey);
    });
    return set;
};

/**
 * Убирает из ВЫБРАННЫХ метрик универсального отчёта (Redux state.table.metrics):
 *  - явно удалённые починкой метрики (deletedIds);
 *  - «мёртвые» кастомные токены _ID_, которых больше нет в справочнике.
 * После этого мёртвые колонки исчезают из окна настроек и таблицы.
 */
export const purgeDeletedMetricsFromRedux = async (
    deletedIds: Array<number | string>
): Promise<void> => {
    try {
        // Ленивый импорт store: статический импорт создаёт циклическую зависимость
        // (утилита грузится в компонентах отчёта раньше, чем инициализируется store)
        const [{ store }, { tableSliceActions }] = await Promise.all([
            import('../../app/providers/redux/store'),
            import('../../app/providers/redux/slices/reports/table/tableSlice'),
        ]);

        const deletedTokens = new Set((deletedIds || []).map((id) => `_${id}_`));
        const state: any = store.getState();
        const existing = getExistingCustomTokens(state);
        const useExisting = existing.size > 0; // защита: не чистим, если справочник не загружен
        const metrics: any[] = state?.table?.metrics ?? [];

        const filtered = metrics.filter((metric: any) => {
            const key = metric?.dataKey;
            if (!key) return true;
            if (deletedTokens.has(key)) return false;
            if (CUSTOM_TOKEN_RE.test(key) && useExisting && !existing.has(key)) return false;
            return true;
        });

        if (filtered.length !== metrics.length) {
            store.dispatch(tableSliceActions.setMetrics({ data: filtered, blockId: null }));
        }
    } catch (e) {
        // состояние недоступно — пропускаем
    }
};

export const purgeDeletedMetricsFromStorage = (deletedIds: Array<number | string>): void => {
    if (!deletedIds || deletedIds.length === 0) return;
    const tokens = deletedIds.map((id) => `_${id}_`);

    Object.keys(window.localStorage)
        .filter((key) => key.startsWith('tableMetricsItemSelected_'))
        .forEach((key) => {
            try {
                const raw = window.localStorage.getItem(key);
                if (!raw) return;
                const list = JSON.parse(raw);
                if (!Array.isArray(list)) return;
                const filtered = list.filter((item: any) => !tokens.includes(item?.dataKey));
                if (filtered.length !== list.length) {
                    window.localStorage.setItem(key, JSON.stringify(filtered));
                }
            } catch (e) {
                // повреждённый ключ пропускаем
            }
        });
};

/**
 * Показывает модалку «отчёт ссылается на удалённые метрики» со списком меток
 * и кнопкой починки. Вызывается из обработчиков ответа рендера при code === 254.
 *
 * @param data          Тело ответа бэкенда (с missing_metrics)
 * @param options.metricsPool Локальный пул метрик для резолва имени удалённой метрики
 * @param options.onRepaired  Колбэк после успешной починки (по умолчанию reload страницы)
 */
export const handleDeletedMetricsError = (
    data: any,
    options?: { metricsPool?: any[]; onRepaired?: () => void }
): void => {
    const missing: MissingMetric[] = (data && data.missing_metrics) || [];
    const pool = options?.metricsPool;

    const lines = missing.map((item, index) => {
        const name = resolveMetricName(item.metric, item.id, pool);
        const usedIn = (item.used_in || [])
            .map((used) => used.name || resolveMetricName(used.metric, used.id, pool))
            .filter(Boolean);
        const usedStr = usedIn.length ? ` — используется в: ${usedIn.join(', ')}` : '';
        return (
            <li key={index}>
                <b>{name}</b> (удалена)
                {usedStr}
            </li>
        );
    });

    Modal.confirm({
        title: 'Отчёт ссылается на удалённые метрики',
        icon: <ExclamationCircleOutlined />,
        width: 560,
        content: (
            <div>
                <p>
                    Некоторые метрики удалены, но используются в расчёте других метрик, поэтому
                    отчёт не строится:
                </p>
                <ul style={{ marginBottom: 12 }}>{lines}</ul>
                <p>
                    «Починить» удалит битые метрики и уберёт ссылки на удалённые из отчётов. Сами
                    отчёты не удаляются.
                </p>
            </div>
        ),
        okText: 'Починить отчёты',
        cancelText: 'Закрыть',
        onOk: () =>
            unApi
                .repairCustomMetrics()
                .then((res) => {
                    const result = res?.data || {};
                    const deleted = result.deleted_metrics || [];
                    // Чистим текущий выбор отчёта (Redux) и localStorage от удалённых/мёртвых метрик
                    purgeDeletedMetricsFromRedux(deleted);
                    purgeDeletedMetricsFromStorage(deleted);
                    showSuccessMessage(
                        `Удалено метрик: ${deleted.length}, исправлено отчётов: ${
                            result.cleaned_reports ?? 0
                        }`
                    );
                    if (options?.onRepaired) {
                        options.onRepaired();
                    }
                })
                .catch(() => {
                    showErrorMessage('Не удалось выполнить починку. Повторите попытку позже.');
                }),
    });
};
